import { useEffect, useCallback } from 'react'

/**
 * useCustomEvent - Hook to emit and listen for custom events.
 *
 * @example
 * // Emitting an event
 * const { emitEvent } = useCustomEvent();
 * emitEvent("customEvent", { userId: 123 });
 *
 * // Listening to an event
 * useCustomEvent<{ userId: 123 }>("customEvent", (data) => console.log(data));
 */
const useCustomEvent = <T>(eventName?: string, eventHandler?: (detail: T) => void) => {
  const emitEvent = useCallback((eventName: string, detail?: T) => {
    const event = new CustomEvent<T>(eventName, { detail })
    window.dispatchEvent(event)
  }, [])

  useEffect(() => {
    if (!eventHandler || !eventName) return

    const handleEvent = (event: CustomEvent<T>) => eventHandler(event.detail)
    window.addEventListener(eventName, handleEvent as EventListener)

    return () => {
      window.removeEventListener(eventName, handleEvent as EventListener)
    }
  }, [eventName, eventHandler])

  return { emitEvent }
}

export default useCustomEvent
